import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TambaLogo from "@/components/TambaLogo";
import { 
  ArrowLeft, 
  User,
  Crown,
  CreditCard,
  LogOut,
  Phone,
  Loader2,
  Check,
  Building2,
  Bell,
  Globe,
  Gift,
  ChevronRight,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = {
  "USD": { symbol: "$", name: "US Dollar" },
  "EUR": { symbol: "€", name: "Euro" },
  "GBP": { symbol: "£", name: "British Pound" },
  "JPY": { symbol: "¥", name: "Japanese Yen" },
  "CAD": { symbol: "CA$", name: "Canadian Dollar" },
  "AUD": { symbol: "A$", name: "Australian Dollar" },
  "INR": { symbol: "₹", name: "Indian Rupee" },
  "CNY": { symbol: "¥", name: "Chinese Yuan" },
  "BRL": { symbol: "R$", name: "Brazilian Real" },
  "MXN": { symbol: "MX$", name: "Mexican Peso" },
  "NGN": { symbol: "₦", name: "Nigerian Naira" },
  "ZAR": { symbol: "R", name: "South African Rand" }
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState("USD");

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    setName(userData.name || "");
    setCurrency(userData.currency || "USD");
  }, [navigate]);

  const handleSaveName = async () => {
    if (!name.trim()) {
      toast.error("Please enter a name");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile/${user.id}?name=${encodeURIComponent(name)}`);
      
      const updatedUser = { ...user, name };
      localStorage.setItem("tamba_user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    try {
      const currencyData = CURRENCIES[newCurrency];
      await axios.post(`${API}/user/currency`, {
        user_id: user.id,
        currency: newCurrency,
        symbol: currencyData.symbol
      });
      
      const updatedUser = { ...user, currency: newCurrency, currency_symbol: currencyData.symbol };
      localStorage.setItem("tamba_user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      setCurrency(newCurrency);
      
      toast.success(`Currency changed to ${currencyData.name}`);
    } catch (error) {
      toast.error("Failed to update currency");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("tamba_user");
    toast.success("Logged out successfully");
    navigate("/");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-heading text-xl font-semibold">Settings</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 gap-3"
        >
          <Card 
            className="border-border/50 hover:border-tamba-blue/30 cursor-pointer transition-all"
            onClick={() => navigate("/bank-connection")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-tamba-blue/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-tamba-blue" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Connected Banks</p>
                <p className="text-xs text-muted-foreground">Link accounts</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card 
            className="border-border/50 hover:border-tamba-coral/30 cursor-pointer transition-all"
            onClick={() => navigate("/budget-limits")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-tamba-coral/10 flex items-center justify-center">
                <Bell className="h-5 w-5 text-tamba-coral" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Budget Alerts</p>
                <p className="text-xs text-muted-foreground">Set limits</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card 
            className="border-border/50 hover:border-purple-500/30 cursor-pointer transition-all"
            onClick={() => navigate("/bills")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-purple-500" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Bill Reminders</p>
                <p className="text-xs text-muted-foreground">Track due dates</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card 
            className="border-border/50 hover:border-green-500/30 cursor-pointer transition-all"
            onClick={() => navigate("/notifications")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <Bell className="h-5 w-5 text-green-500" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Notifications</p>
                <p className="text-xs text-muted-foreground">Email, SMS, Push</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card 
            className="border-border/50 hover:border-tamba-yellow/30 cursor-pointer transition-all"
            onClick={() => navigate("/referral")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-tamba-yellow/10 flex items-center justify-center">
                <Gift className="h-5 w-5 text-tamba-yellow" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Refer & Earn</p>
                <p className="text-xs text-muted-foreground">Free months</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card 
            className="border-border/50 hover:border-tamba-teal/30 cursor-pointer transition-all"
            onClick={() => navigate("/pricing")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-tamba-teal/10 flex items-center justify-center">
                <Crown className="h-5 w-5 text-tamba-teal" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Subscription</p>
                <p className="text-xs text-muted-foreground">{user.subscription_status === "premium" ? "Premium" : "Upgrade"}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </motion.div>

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile
              </CardTitle>
              <CardDescription>Manage your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Name</Label>
                <div className="flex gap-3 mt-2">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your name"
                    className="flex-1"
                    data-testid="name-input"
                  />
                  <Button 
                    onClick={handleSaveName}
                    disabled={saving}
                    className="rounded-full"
                    data-testid="save-name-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-muted-foreground">Phone Number</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{user.phone}</span>
                  <Check className="h-4 w-4 text-accent" />
                </div>
              </div>

              <Separator />

              <div>
                <Label className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Currency
                </Label>
                <Select value={currency} onValueChange={handleCurrencyChange}>
                  <SelectTrigger className="mt-2" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCIES).map(([code, data]) => (
                      <SelectItem key={code} value={code}>
                        <span className="flex items-center gap-2">
                          <span className="w-6">{data.symbol}</span>
                          <span>{data.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  All amounts will be displayed in {CURRENCIES[currency]?.name}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Subscription Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Crown className="h-5 w-5 text-tamba-yellow" />
                Subscription
              </CardTitle>
              <CardDescription>Manage your subscription plan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    user.subscription_status === "premium" 
                      ? "bg-tamba-yellow/20" 
                      : "bg-muted"
                  }`}>
                    {user.subscription_status === "premium" ? (
                      <Crown className="h-5 w-5 text-tamba-yellow" />
                    ) : (
                      <CreditCard className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">
                      {user.subscription_status === "premium" ? "Premium Plan" : "Free Plan"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user.subscription_status === "premium" 
                        ? "Full access to all features" 
                        : "Limited features"
                      }
                    </p>
                  </div>
                </div>
                
                {user.subscription_status !== "premium" && (
                  <Button 
                    onClick={() => navigate("/pricing")}
                    className="rounded-full bg-tamba-blue hover:bg-tamba-blue/90"
                    data-testid="upgrade-btn"
                  >
                    Upgrade
                  </Button>
                )}
              </div>
              
              {user.subscription_status !== "premium" && (
                <div className="mt-4 p-4 border border-accent/30 rounded-xl bg-accent/5">
                  <h4 className="font-semibold mb-2">Premium Benefits</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>✓ Unlimited AI financial conversations</li>
                    <li>✓ Advanced spending insights</li>
                    <li>✓ Priority support</li>
                    <li>✓ Export financial reports</li>
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Account Actions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Account</CardTitle>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Log Out
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default SettingsPage;
